import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { DragEvent } from "react"
import type { DockDropValue } from "./dock-drop-context"
import { collectDroppedFiles, isFileDrag } from "./dropped-files"
import type { AcceptAttachments } from "./use-accept-attachments"

export interface DockFileDropHandlers {
  onDragEnter: (event: DragEvent<HTMLElement>) => void
  onDragOver: (event: DragEvent<HTMLElement>) => void
  onDragLeave: (event: DragEvent<HTMLElement>) => void
  onDrop: (event: DragEvent<HTMLElement>) => void
}

function isInsideDock(event: DragEvent<HTMLElement>): boolean {
  return event.target instanceof Node && event.currentTarget.contains(event.target)
}

type DragState = "attachable" | "refused" | null

export const DRAG_IDLE_RESET_MS = 1500
const DRAG_IDLE_CHECK_MS = 500

export function useDockFileDrop(canAcceptFiles: boolean) {
  const acceptAttachmentsRef = useRef<AcceptAttachments | null>(null)
  const depthRef = useRef(0)
  const [dragState, setDragState] = useState<DragState>(null)
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  const lastActivityRef = useRef(0)
  const dropValue = useMemo<DockDropValue>(() => ({ acceptAttachmentsRef }), [])

  const reset = useCallback(() => {
    clearInterval(idleTimerRef.current)
    idleTimerRef.current = undefined
    depthRef.current = 0
    setDragState(null)
  }, [])

  useEffect(() => reset, [reset])

  const handlers = useMemo<DockFileDropHandlers>(() => {
    const canAttach = () => canAcceptFiles && acceptAttachmentsRef.current !== null
    const markActive = () => {
      lastActivityRef.current = Date.now()
      if (idleTimerRef.current !== undefined) return
      idleTimerRef.current = setInterval(() => {
        if (Date.now() - lastActivityRef.current >= DRAG_IDLE_RESET_MS) reset()
      }, DRAG_IDLE_CHECK_MS)
    }
    const startDrag = () => setDragState(canAttach() ? "attachable" : "refused")
    return {
      onDragEnter: (event) => {
        if (!isFileDrag(event.dataTransfer)) return
        event.preventDefault()
        if (!isInsideDock(event)) return
        depthRef.current += 1
        markActive()
        startDrag()
      },
      onDragOver: (event) => {
        if (!isFileDrag(event.dataTransfer)) return
        event.preventDefault()
        if (!isInsideDock(event)) return
        markActive()
        if (depthRef.current === 0) {
          depthRef.current = 1
          startDrag()
        }
        event.dataTransfer.dropEffect = canAttach() ? "copy" : "none"
      },
      onDragLeave: (event) => {
        if (!isFileDrag(event.dataTransfer) || !isInsideDock(event)) return
        depthRef.current = Math.max(0, depthRef.current - 1)
        if (depthRef.current === 0) reset()
      },
      onDrop: (event) => {
        if (!isFileDrag(event.dataTransfer)) return
        event.preventDefault()
        reset()
        if (!isInsideDock(event) || !canAttach()) return
        const { files, folders } = collectDroppedFiles(event.dataTransfer)
        if (files.length > 0 || folders.length > 0) acceptAttachmentsRef.current?.(files, "drop", folders)
      },
    }
  }, [canAcceptFiles, reset])

  return { dropValue, handlers, isDragging: dragState !== null, isAttachable: dragState === "attachable" }
}
